import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  QbNodeAction,
  QbNodeView,
  nodeTermCount,
  pathKey,
  plural,
} from '../query-builder.model';

/** and/or are a choice; anything else is shown as it is, never rewritten. */
function known(operator: string): 'and' | 'or' | null {
  const op = (operator ?? 'and').toLowerCase();
  return op === 'and' || op === 'or' ? op : null;
}

/**
 * One node of the query, drawn where it sits. The zones are a projection and
 * cannot show every shape; this shows all of them, so a query never has to be
 * reshaped to stay editable — which is what used to change what it matched.
 *
 * It owns no state and emits no tree: the builder holds the tree, declares the
 * pill template this renders, and applies every action.
 */
@Component({
  selector: 'app-query-node',
  templateUrl: './query-node.component.html',
  styleUrls: ['./query-node.component.scss'],
})
export class QueryNodeComponent implements OnChanges {
  @Input() node: QbNodeView = {
    path: [],
    operator: 'and',
    terms: [],
    children: [],
  };
  @Input() editing = false;
  /**
   * The builder's own pill list. Passed rather than reimplemented so its
   * expressions still resolve against the builder — one owner of picker state,
   * one owner of every `treeChange`.
   */
  @Input() pills: TemplateRef<unknown> | null = null;

  @Output() nodeAction = new EventEmitter<QbNodeAction>();

  /** Built once per node so `ngTemplateOutlet` is not handed a new object per pass. */
  termsCtx: Record<string, unknown> = {};
  excludedCtx: Record<string, unknown> = {};

  ngOnChanges(): void {
    const key = pathKey(this.node.path);
    this.termsCtx = {
      terms: this.node.terms,
      path: this.node.path,
      slot: 'terms',
      zone: `${key}:terms`,
      placeholder: 'Search or type a term',
    };
    this.excludedCtx = {
      terms: [],
      addOnly: true,
      negate: true,
      path: this.node.path,
      slot: 'excluded',
      zone: `${key}:excluded`,
      label: '+ except',
      placeholder: 'A term to keep out',
    };
  }

  get isRoot(): boolean {
    return !this.node.path.length;
  }

  get canSetOperator(): boolean {
    return this.editing && !!known(this.node.operator);
  }

  get operatorLabel(): string {
    const op = known(this.node.operator);
    if (op === 'or') return 'any one of these';
    if (op === 'and') return 'all of these';
    return this.node.operator;
  }

  get countLabel(): string {
    return plural(nodeTermCount(this.node), 'term');
  }

  setOperator(operator: 'and' | 'or'): void {
    if (this.node.operator === operator) return;
    this.nodeAction.emit({ kind: 'operator', path: this.node.path, operator });
  }

  addGroup(): void {
    this.nodeAction.emit({ kind: 'add-group', path: this.node.path });
  }

  remove(): void {
    this.nodeAction.emit({ kind: 'remove', path: this.node.path });
  }

  trackByPath(_: number, child: QbNodeView): string {
    return pathKey(child.path);
  }
}
